/**
 * Pure statistical functions for workspace analytics.
 * All functions accept plain number[] arrays — no recording/React dependencies.
 */

/**
 * Filter data points to those within a time window.
 */
export function filterByWindow(dataPoints, startTime, endTime) {
  if (!dataPoints || startTime == null || endTime == null) return dataPoints || [];
  return dataPoints.filter((dp) => dp.time >= startTime && dp.time <= endTime);
}

/**
 * Extract numeric values from a recording's sensor data, optionally windowed.
 */
export function extractValues(recording, sensorKey, startTime, endTime) {
  if (!recording?.data?.[sensorKey]) return [];
  let points = recording.data[sensorKey];
  if (startTime != null && endTime != null) {
    points = filterByWindow(points, startTime, endTime);
  }
  return points.map((dp) => Number(dp.value)).filter((v) => !Number.isNaN(v));
}

/**
 * Compute all descriptive statistics for an array of numbers.
 */
export function descriptiveStats(values) {
  if (!values || values.length === 0) return null;

  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;
  const sum = sorted.reduce((s, v) => s + v, 0);
  const mean = sum / n;

  const min = sorted[0];
  const max = sorted[n - 1];
  const delta = max - min;

  // Sample standard deviation (n-1)
  const variance = n > 1 ? sorted.reduce((s, v) => s + (v - mean) ** 2, 0) / (n - 1) : 0;
  const stdev = Math.sqrt(variance);

  const q1 = percentile(sorted, 0.25);
  const median = percentile(sorted, 0.5);
  const q3 = percentile(sorted, 0.75);
  const iqr = q3 - q1;

  const mode = computeMode(sorted);

  return { min, max, delta, mean, stdev, q1, median, q3, iqr, mode, count: n };
}

/**
 * Inclusive percentile (matches Excel QUARTILE.INC).
 */
function percentile(sorted, p) {
  const n = sorted.length;
  if (n === 0) return 0;
  if (n === 1) return sorted[0];
  const rank = p * (n - 1);
  const lower = Math.floor(rank);
  const upper = Math.ceil(rank);
  const frac = rank - lower;
  return sorted[lower] + frac * (sorted[upper] - sorted[lower]);
}

/**
 * Most frequently occurring value. Returns null if no repeats.
 */
function computeMode(sorted) {
  const freq = new Map();
  let maxCount = 0;
  let modeVal = null;
  for (const v of sorted) {
    const c = (freq.get(v) || 0) + 1;
    freq.set(v, c);
    if (c > maxCount) {
      maxCount = c;
      modeVal = v;
    }
  }
  return maxCount > 1 ? modeVal : null;
}

// --- Inferential Statistics ---

/**
 * F-test for equality of variances (two-tailed).
 */
export function fTest(sample1, sample2) {
  const n1 = sample1.length;
  const n2 = sample2.length;
  if (n1 < 2 || n2 < 2) return { fStatistic: NaN, pValue: NaN, result: 'Insufficient data' };

  const var1 = sampleVariance(sample1);
  const var2 = sampleVariance(sample2);
  if (var2 === 0 && var1 === 0) return { fStatistic: 1, pValue: 1, result: 'Equal Variance' };
  if (var2 === 0) return { fStatistic: Infinity, pValue: 0, result: 'Unequal Variance' };

  const f = var1 / var2;
  const df1 = n1 - 1;
  const df2 = n2 - 1;
  const pValue = 2 * Math.min(fCdf(f, df1, df2), 1 - fCdf(f, df1, df2));

  return {
    fStatistic: f,
    pValue,
    result: pValue < 0.05 ? 'Unequal Variance' : 'Equal Variance',
  };
}

/**
 * T-test for difference in means.
 * Uses Welch's or Student's based on equalVariance flag.
 */
export function tTest(sample1, sample2, equalVariance = true) {
  const n1 = sample1.length;
  const n2 = sample2.length;
  if (n1 < 2 || n2 < 2) return { tStatistic: NaN, pValue: NaN, result: 'Insufficient data', variant: 'N/A' };

  const mean1 = sample1.reduce((s, v) => s + v, 0) / n1;
  const mean2 = sample2.reduce((s, v) => s + v, 0) / n2;
  const var1 = sampleVariance(sample1);
  const var2 = sampleVariance(sample2);

  let t, df;
  const variant = equalVariance ? "Student's" : "Welch's";

  if (equalVariance) {
    // Pooled variance
    const sp = ((n1 - 1) * var1 + (n2 - 1) * var2) / (n1 + n2 - 2);
    const se = Math.sqrt(sp * (1 / n1 + 1 / n2));
    t = se === 0 ? 0 : (mean1 - mean2) / se;
    df = n1 + n2 - 2;
  } else {
    // Welch's
    const se = Math.sqrt(var1 / n1 + var2 / n2);
    t = se === 0 ? 0 : (mean1 - mean2) / se;
    const num = (var1 / n1 + var2 / n2) ** 2;
    const den = (var1 / n1) ** 2 / (n1 - 1) + (var2 / n2) ** 2 / (n2 - 1);
    df = den === 0 ? n1 + n2 - 2 : num / den;
  }

  const pValue = 2 * (1 - tCdf(Math.abs(t), df));

  return {
    tStatistic: t,
    pValue,
    df,
    variant,
    result: pValue < 0.05 ? 'Significant' : 'Not Significant',
  };
}

/**
 * Mann-Whitney U test (non-parametric).
 */
export function mannWhitneyU(sample1, sample2) {
  const n1 = sample1.length;
  const n2 = sample2.length;
  if (n1 === 0 || n2 === 0) return { u: NaN, z: NaN, pValue: NaN, result: 'Insufficient data', n1, n2 };

  // Combined ranking with average ties
  const combined = [
    ...sample1.map((v) => ({ value: v, group: 1 })),
    ...sample2.map((v) => ({ value: v, group: 2 })),
  ].sort((a, b) => a.value - b.value);

  const N = combined.length;
  const ranks = new Array(N);

  for (let i = 0; i < N; ) {
    let j = i;
    while (j < N && combined[j].value === combined[i].value) j++;
    const avgRank = (i + 1 + j) / 2; // 1-indexed average
    for (let k = i; k < j; k++) ranks[k] = avgRank;
    i = j;
  }

  let r1 = 0;
  let r2 = 0;
  for (let i = 0; i < N; i++) {
    if (combined[i].group === 1) r1 += ranks[i];
    else r2 += ranks[i];
  }

  const u1 = r1 - (n1 * (n1 + 1)) / 2;
  const u2 = r2 - (n2 * (n2 + 1)) / 2;
  const u = Math.min(u1, u2);

  const meanU = (n1 * n2) / 2;
  const sdU = Math.sqrt((n1 * n2 * (n1 + n2 + 1)) / 12);
  const z = sdU === 0 ? 0 : (u - meanU) / sdU;
  const pValue = 2 * (1 - normalCdf(Math.abs(z)));

  return { u, z, pValue, n1, n2, r1, r2, result: pValue < 0.05 ? 'Significant' : 'Not Significant' };
}

/**
 * Skewness (sample, Fisher's definition).
 */
export function skewness(values) {
  const n = values.length;
  if (n < 3) return { value: NaN, interpretation: 'Insufficient data' };

  const mean = values.reduce((s, v) => s + v, 0) / n;
  const m2 = values.reduce((s, v) => s + (v - mean) ** 2, 0) / n;
  const m3 = values.reduce((s, v) => s + (v - mean) ** 3, 0) / n;
  const s = m2 === 0 ? 0 : m3 / m2 ** 1.5;

  // Adjusted (Fisher) skewness
  const adjusted = (Math.sqrt(n * (n - 1)) / (n - 2)) * s;

  let interpretation;
  const abs = Math.abs(adjusted);
  if (abs < 0.5) interpretation = 'Symmetric';
  else if (abs < 1.0) interpretation = adjusted > 0 ? 'Moderate Right Skew' : 'Moderate Left Skew';
  else interpretation = adjusted > 0 ? 'Highly Right Skewed' : 'Highly Left Skewed';

  return { value: adjusted, interpretation };
}

/**
 * Excess kurtosis (sample).
 */
export function kurtosis(values) {
  const n = values.length;
  if (n < 4) return { value: NaN, interpretation: 'Insufficient data' };

  const mean = values.reduce((s, v) => s + v, 0) / n;
  const m2 = values.reduce((s, v) => s + (v - mean) ** 2, 0) / n;
  const m4 = values.reduce((s, v) => s + (v - mean) ** 4, 0) / n;
  const k = m2 === 0 ? 0 : m4 / m2 ** 2 - 3; // excess kurtosis

  let interpretation;
  if (k < -1) interpretation = 'Platykurtic';
  else if (k <= 1) interpretation = 'Mesokurtic';
  else interpretation = 'Leptokurtic';

  return { value: k, interpretation };
}

// --- Correlation ---

/**
 * Pearson correlation coefficient between two arrays.
 */
export function pearsonCorrelation(xs, ys) {
  const n = Math.min(xs.length, ys.length);
  if (n < 3) return { r: NaN, interpretation: 'Insufficient data' };

  const meanX = xs.slice(0, n).reduce((s, v) => s + v, 0) / n;
  const meanY = ys.slice(0, n).reduce((s, v) => s + v, 0) / n;

  let sumXY = 0, sumX2 = 0, sumY2 = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - meanX;
    const dy = ys[i] - meanY;
    sumXY += dx * dy;
    sumX2 += dx * dx;
    sumY2 += dy * dy;
  }

  const denom = Math.sqrt(sumX2 * sumY2);
  const r = denom === 0 ? 0 : sumXY / denom;

  const abs = Math.abs(r);
  let interpretation;
  if (abs < 0.3) interpretation = 'Weak';
  else if (abs < 0.7) interpretation = 'Moderate';
  else interpretation = 'Strong';
  interpretation += r >= 0 ? ' Positive' : ' Negative';

  return { r, interpretation, n };
}

/**
 * Spearman rank correlation coefficient.
 */
export function spearmanCorrelation(xs, ys) {
  const n = Math.min(xs.length, ys.length);
  if (n < 3) return { rho: NaN, interpretation: 'Insufficient data' };

  const rankArr = (arr) => {
    const indexed = arr.slice(0, n).map((v, i) => ({ v, i })).sort((a, b) => a.v - b.v);
    const ranks = new Array(n);
    for (let i = 0; i < n; ) {
      let j = i;
      while (j < n && indexed[j].v === indexed[i].v) j++;
      const avg = (i + 1 + j) / 2;
      for (let k = i; k < j; k++) ranks[indexed[k].i] = avg;
      i = j;
    }
    return ranks;
  };

  const ranksX = rankArr(xs);
  const ranksY = rankArr(ys);
  const result = pearsonCorrelation(ranksX, ranksY);
  return { rho: result.r, interpretation: result.interpretation, n };
}

// --- Anomaly Detection ---

/**
 * Detect outliers using IQR fences.
 * Returns data points outside [Q1 - k*IQR, Q3 + k*IQR].
 */
export function detectOutliersIQR(dataPoints, k = 1.5) {
  if (!dataPoints || dataPoints.length === 0) return { outliers: [], lowerFence: 0, upperFence: 0 };

  const values = dataPoints.map((dp) => Number(dp.value)).filter((v) => !Number.isNaN(v));
  const sorted = [...values].sort((a, b) => a - b);
  const q1 = percentile(sorted, 0.25);
  const q3 = percentile(sorted, 0.75);
  const iqr = q3 - q1;
  const lowerFence = q1 - k * iqr;
  const upperFence = q3 + k * iqr;

  const outliers = dataPoints.filter((dp) => {
    const v = Number(dp.value);
    return !Number.isNaN(v) && (v < lowerFence || v > upperFence);
  });

  return { outliers, lowerFence, upperFence, q1, q3, iqr };
}

/**
 * Detect outliers using z-score threshold.
 */
export function detectOutliersZScore(dataPoints, threshold = 3) {
  if (!dataPoints || dataPoints.length < 2) return { outliers: [] };

  const values = dataPoints.map((dp) => Number(dp.value)).filter((v) => !Number.isNaN(v));
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  const std = Math.sqrt(values.reduce((s, v) => s + (v - mean) ** 2, 0) / (values.length - 1));
  if (std === 0) return { outliers: [], mean, std };

  const outliers = dataPoints.filter((dp) => {
    const v = Number(dp.value);
    return !Number.isNaN(v) && Math.abs((v - mean) / std) > threshold;
  });

  return { outliers, mean, std };
}

// --- Rate of Change ---

/**
 * Compute first derivative (rate of change) of sensor data.
 * Returns array of { time, value } where value is units per second.
 */
export function rateOfChange(dataPoints) {
  if (!dataPoints || dataPoints.length < 2) return [];

  const result = [];
  for (let i = 1; i < dataPoints.length; i++) {
    const dt = (dataPoints[i].time - dataPoints[i - 1].time) / 1000; // seconds
    if (dt <= 0) continue;
    const dv = Number(dataPoints[i].value) - Number(dataPoints[i - 1].value);
    result.push({ time: dataPoints[i].time, value: dv / dt });
  }
  return result;
}

// --- AQI Calculation ---

/**
 * Compute EPA AQI from a concentration value using standard breakpoints.
 */
export function computeAQI(pollutant, concentration) {
  const breakpoints = AQI_BREAKPOINTS[pollutant];
  if (!breakpoints) return null;

  for (const bp of breakpoints) {
    if (concentration >= bp.cLow && concentration <= bp.cHigh) {
      const aqi = ((bp.iHigh - bp.iLow) / (bp.cHigh - bp.cLow)) * (concentration - bp.cLow) + bp.iLow;
      return { aqi: Math.round(aqi), category: bp.category, color: bp.color };
    }
  }
  // Above the highest breakpoint
  const last = breakpoints[breakpoints.length - 1];
  return { aqi: last.iHigh, category: 'Hazardous', color: '#7E0023' };
}

const AQI_BREAKPOINTS = {
  'pm2.5': [
    { cLow: 0, cHigh: 12.0, iLow: 0, iHigh: 50, category: 'Good', color: '#00E400' },
    { cLow: 12.1, cHigh: 35.4, iLow: 51, iHigh: 100, category: 'Moderate', color: '#FFFF00' },
    { cLow: 35.5, cHigh: 55.4, iLow: 101, iHigh: 150, category: 'Unhealthy (Sensitive)', color: '#FF7E00' },
    { cLow: 55.5, cHigh: 150.4, iLow: 151, iHigh: 200, category: 'Unhealthy', color: '#FF0000' },
    { cLow: 150.5, cHigh: 250.4, iLow: 201, iHigh: 300, category: 'Very Unhealthy', color: '#8F3F97' },
    { cLow: 250.5, cHigh: 500.4, iLow: 301, iHigh: 500, category: 'Hazardous', color: '#7E0023' },
  ],
  'pm10': [
    { cLow: 0, cHigh: 54, iLow: 0, iHigh: 50, category: 'Good', color: '#00E400' },
    { cLow: 55, cHigh: 154, iLow: 51, iHigh: 100, category: 'Moderate', color: '#FFFF00' },
    { cLow: 155, cHigh: 254, iLow: 101, iHigh: 150, category: 'Unhealthy (Sensitive)', color: '#FF7E00' },
    { cLow: 255, cHigh: 354, iLow: 151, iHigh: 200, category: 'Unhealthy', color: '#FF0000' },
    { cLow: 355, cHigh: 424, iLow: 201, iHigh: 300, category: 'Very Unhealthy', color: '#8F3F97' },
    { cLow: 425, cHigh: 604, iLow: 301, iHigh: 500, category: 'Hazardous', color: '#7E0023' },
  ],
  'co2': [
    { cLow: 0, cHigh: 600, iLow: 0, iHigh: 50, category: 'Good', color: '#00E400' },
    { cLow: 601, cHigh: 1000, iLow: 51, iHigh: 100, category: 'Moderate', color: '#FFFF00' },
    { cLow: 1001, cHigh: 1500, iLow: 101, iHigh: 150, category: 'Unhealthy (Sensitive)', color: '#FF7E00' },
    { cLow: 1501, cHigh: 2500, iLow: 151, iHigh: 200, category: 'Unhealthy', color: '#FF0000' },
    { cLow: 2501, cHigh: 5000, iLow: 201, iHigh: 300, category: 'Very Unhealthy', color: '#8F3F97' },
    { cLow: 5001, cHigh: 50000, iLow: 301, iHigh: 500, category: 'Hazardous', color: '#7E0023' },
  ],
};

/**
 * Map a sensor key suffix to an AQI pollutant key.
 */
export function sensorKeyToPollutant(sensorKey) {
  const metric = (sensorKey || '').split(':').pop().toLowerCase();
  if (metric.includes('pm2.5') || metric === 'ae pm2.5' || metric === 'sp pm2.5' || metric === 'sp_pm25') return 'pm2.5';
  if (metric.includes('pm10') || metric === 'ae pm10' || metric === 'sp_pm10') return 'pm10';
  if (metric.includes('co2') || metric === 'co2_data' || metric === 'co2(ppm)') return 'co2';
  return null;
}

// --- Threshold Exceedance ---

/**
 * Compute total time spent exceeding each active rule's threshold.
 * Returns array of { ruleName, totalMs, totalMinutes, exceedanceCount }.
 */
export function thresholdExceedance(dataPoints, rules) {
  if (!dataPoints || dataPoints.length < 2 || !rules?.length) return [];

  return rules.map((rule) => {
    const threshold = parseFloat(rule.value);
    let totalMs = 0;
    let exceedanceCount = 0;
    let inExceedance = false;

    for (let i = 0; i < dataPoints.length; i++) {
      const v = Number(dataPoints[i].value);
      const violated =
        (rule.operator === 'greater' && v > threshold) ||
        (rule.operator === 'lesser' && v < threshold);

      if (violated) {
        if (!inExceedance) {
          exceedanceCount++;
          inExceedance = true;
        }
        if (i + 1 < dataPoints.length) {
          totalMs += dataPoints[i + 1].time - dataPoints[i].time;
        }
      } else {
        inExceedance = false;
      }
    }

    return {
      ruleName: rule.name,
      totalMs,
      totalMinutes: totalMs / 60000,
      exceedanceCount,
    };
  });
}

// --- Histogram binning ---

/**
 * Bin values into a histogram.
 */
export function histogram(values, binCount = 20) {
  if (!values || values.length === 0) return { bins: [], min: 0, max: 0 };

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const binWidth = range / binCount;

  const bins = Array.from({ length: binCount }, (_, i) => ({
    binStart: min + i * binWidth,
    binEnd: min + (i + 1) * binWidth,
    count: 0,
  }));

  for (const v of values) {
    let idx = Math.floor((v - min) / binWidth);
    if (idx >= binCount) idx = binCount - 1;
    bins[idx].count++;
  }

  return { bins, min, max, binWidth };
}

// --- Distribution helpers (approximations) ---

function sampleVariance(arr) {
  const n = arr.length;
  if (n < 2) return 0;
  const mean = arr.reduce((s, v) => s + v, 0) / n;
  return arr.reduce((s, v) => s + (v - mean) ** 2, 0) / (n - 1);
}

/**
 * Standard normal CDF (Abramowitz & Stegun approximation).
 */
function normalCdf(x) {
  if (x < -8) return 0;
  if (x > 8) return 1;
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741, a4 = -1.453152027, a5 = 1.061405429;
  const p = 0.3275911;
  const sign = x < 0 ? -1 : 1;
  const t = 1 / (1 + p * Math.abs(x));
  const y = 1 - ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-x * x / 2);
  return 0.5 * (1 + sign * y);
}

/**
 * Student's t CDF approximation using regularized incomplete beta function.
 */
function tCdf(t, df) {
  const x = df / (df + t * t);
  return 1 - 0.5 * regIncBeta(x, df / 2, 0.5);
}

/**
 * F distribution CDF approximation.
 */
function fCdf(f, df1, df2) {
  if (f <= 0) return 0;
  const x = df2 / (df2 + df1 * f);
  return 1 - regIncBeta(x, df2 / 2, df1 / 2);
}

/**
 * Regularized incomplete beta function (continued fraction approximation).
 */
function regIncBeta(x, a, b) {
  if (x <= 0) return 0;
  if (x >= 1) return 1;

  // Use symmetry if needed for convergence
  if (x > (a + 1) / (a + b + 2)) {
    return 1 - regIncBeta(1 - x, b, a);
  }

  const lnBeta = lnGamma(a) + lnGamma(b) - lnGamma(a + b);
  const front = Math.exp(Math.log(x) * a + Math.log(1 - x) * b - lnBeta) / a;

  // Lentz's continued fraction
  let f = 1, c = 1, d = 1 - (a + b) * x / (a + 1);
  if (Math.abs(d) < 1e-30) d = 1e-30;
  d = 1 / d;
  f = d;

  for (let m = 1; m <= 200; m++) {
    // Even step
    let num = m * (b - m) * x / ((a + 2 * m - 1) * (a + 2 * m));
    d = 1 + num * d; if (Math.abs(d) < 1e-30) d = 1e-30; d = 1 / d;
    c = 1 + num / c; if (Math.abs(c) < 1e-30) c = 1e-30;
    f *= d * c;

    // Odd step
    num = -(a + m) * (a + b + m) * x / ((a + 2 * m) * (a + 2 * m + 1));
    d = 1 + num * d; if (Math.abs(d) < 1e-30) d = 1e-30; d = 1 / d;
    c = 1 + num / c; if (Math.abs(c) < 1e-30) c = 1e-30;
    f *= d * c;

    if (Math.abs(d * c - 1) < 1e-8) break;
  }

  return front * f;
}

/**
 * Log-gamma function (Lanczos approximation).
 */
function lnGamma(z) {
  const g = 7;
  const coef = [0.99999999999980993, 676.5203681218851, -1259.1392167224028, 771.32342877765313,
    -176.61502916214059, 12.507343278686905, -0.13857109526572012, 9.9843695780195716e-6, 1.5056327351493116e-7];
  if (z < 0.5) {
    return Math.log(Math.PI / Math.sin(Math.PI * z)) - lnGamma(1 - z);
  }
  z -= 1;
  let x = coef[0];
  for (let i = 1; i < g + 2; i++) x += coef[i] / (z + i);
  const t = z + g + 0.5;
  return 0.5 * Math.log(2 * Math.PI) + (z + 0.5) * Math.log(t) - t + Math.log(x);
}
